import { useState } from 'react';
import ProductLookup from '../components/ProductLookup.jsx';
import { api, money } from '../api.js';
import NumBox from '../components/NumBox.jsx';

/**
 * Пълна редакция на продажба без касова бележка (плащане по банков път
 * или с ППП).
 *
 * Чл. 3, ал. 1 от Наредба № Н-18 изважда такава продажба от фискалния
 * режим изцяло — за нея никога не е излизал бон, затова се поправя
 * пряко, не само със сторно (виж 101_editable_sale_bez_bon.sql).
 * Сървърът (editSale) отказва същото за всяко друго плащане.
 *
 * Смяна на стоката на съществуващ ред тук НЕ се позволява — стоката вече
 * е записана като продадена под УНП на документа (т. 12). Вместо
 * преизвързване редът се анулира и се добавя нов, точно както е при
 * обикновена продажба (виж „Смени само при доставка").
 */
export default function SaleEdit({ doc, onDone, onCancel }) {
  const [rows, setRows] = useState(() => doc.items.map((it) => ({
    key: `r${it.id}`,
    id: it.id,
    description: it.description,
    unit: it.unit,
    quantity: String(Number(it.quantity)),
    price: String(Number(it.price)),
    vatRate: Number(it.vat_rate),
    note: it.note ?? '',
    isGift: Number(it.is_gift) === 1,
    cancelled: Number(it.is_cancelled) === 1,
    cancelNow: false,
  })));
  const [noteDraft, setNoteDraft] = useState(doc.note ?? '');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const patch = (key, p) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));

  async function addProduct(productId) {
    if (!productId) return;
    try {
      const p = await api.get(`/api/products/${productId}`);
      const price = p.retail_price ?? p.wholesale_price ?? 0;
      setRows((rs) => [...rs, {
        key: `n${p.id}-${Date.now()}`,
        id: null,
        productId: p.id,
        description: p.name,
        unit: p.unit,
        quantity: '1',
        price: String(Number(price)),
        vatRate: Number(p.vat_rate ?? 20),
        note: '',
        isGift: false,
        cancelled: false,
        cancelNow: false,
      }]);
    } catch (err) {
      setError(err.message);
    }
  }

  // Сумите тук са без ДДС, начислен отгоре — както навсякъде другаде в
  // редакцията и въвеждането на документи.
  const round2 = (n) => Math.round(n * 100) / 100;
  let netTotal = 0;
  let vat = 0;
  for (const r of rows) {
    if (r.cancelled || r.cancelNow) continue;
    const line = round2(Number(r.quantity || 0) * Number(r.price || 0));
    netTotal = round2(netTotal + line);
    vat = round2(vat + round2((line * Number(r.vatRate || 0)) / 100));
  }
  const total = round2(netTotal + vat);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if ((doc.note ?? '') !== noteDraft) {
        await api.patch(`/api/documents/${doc.id}/note`, { note: noteDraft || null });
      }
      const items = [];
      for (const r of rows) {
        if (r.cancelled) continue;              // вече анулиран — не се пипа
        if (r.cancelNow) { items.push({ id: r.id, isCancelled: true }); continue; }
        if (r.id) {
          items.push({
            id: r.id,
            quantity: Number(r.quantity || 0),
            price: Number(r.price || 0),
            vatRate: r.vatRate,
            note: r.note || null,
          });
        } else {
          items.push({
            productId: r.productId,
            quantity: Number(r.quantity || 0),
            price: Number(r.price || 0),
            vatRate: r.vatRate,
            note: r.note || undefined,
          });
        }
      }
      if (items.length > 0) {
        await api.patch(`/api/documents/${doc.id}/sale-items`, { items });
      }
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">Редакция на продажбата</div>

      <div className="card-body" style={{ paddingBottom: 8 }}>
        <p className="hint" style={{ marginTop: 0 }}>
          Плащането е без касова бележка — количество, цена и редове могат
          да се поправят пряко. Смяна на стоката на съществуващ ред не е
          възможна: анулирайте реда (✕) и добавете нов за вярната стока.
        </p>
        <label className="field">
          Бележка към документа
          <textarea rows={2} value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)} placeholder="няма бележка" />
        </label>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="num" style={{ width: 34 }}>№</th>
              <th>Наименование</th>
              <th>Бележка на реда</th>
              <th className="num" style={{ width: 100 }}>Количество</th>
              <th className="num" style={{ width: 120 }}>Ед. цена без ДДС</th>
              <th className="num" style={{ width: 70 }}>ДДС</th>
              <th className="num" style={{ width: 110 }}>Сума без ДДС</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, index) => {
              const removed = r.cancelled || r.cancelNow;
              const style = removed ? { textDecoration: 'line-through' } : undefined;
              return (
                <tr key={r.key} className={removed ? 'muted' : undefined}>
                  <td className="num muted">{index + 1}</td>
                  <td style={style}>
                    {r.description}
                    {r.id ? '' : <span className="badge" style={{ marginLeft: 6 }}>нов</span>}
                    {r.cancelled && <span className="badge muted" style={{ marginLeft: 6 }}>анулиран</span>}
                    {r.cancelNow && <span className="badge muted" style={{ marginLeft: 6 }}>ще се анулира</span>}
                  </td>
                  <td style={style}>
                    {removed ? (r.note || '—') : (
                      <input type="text" value={r.note}
                        onChange={(e) => patch(r.key, { note: e.target.value })}
                        style={{ width: '100%' }} />
                    )}
                  </td>
                  <td className="num">
                    {removed ? quantity2(r.quantity) : (
                      <NumBox className="num" value={r.quantity}
                        onChange={(v) => patch(r.key, { quantity: v })} />
                    )}
                  </td>
                  <td className="num">
                    {removed ? Number(r.price).toFixed(4) : (
                      <NumBox className="price num" value={r.price}
                        onChange={(v) => patch(r.key, { price: v })} />
                    )}
                  </td>
                  <td className="num muted">{r.vatRate}%</td>
                  <td className="num" style={style}>
                    {money(round2(Number(r.quantity || 0) * Number(r.price || 0)), doc.currency)}
                  </td>
                  <td>
                    {r.id && !r.cancelled && !r.cancelNow && (
                      <button className="link" title="анулирай реда"
                        onClick={() => patch(r.key, { cancelNow: true })}>×</button>
                    )}
                    {r.cancelNow && (
                      <button className="link" title="отмени анулирането"
                        onClick={() => patch(r.key, { cancelNow: false })}>↺</button>
                    )}
                    {!r.id && (
                      <button className="link" title="махни новия ред"
                        onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>×</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="empty">Продажбата остава без редове</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card-body">
        <label className="field" style={{ maxWidth: 460 }}>
          Добавяне на нов ред
          <ProductLookup value={null} label=""
            placeholder="търсене по име или код"
            onPick={(id) => addProduct(id)} />
        </label>

        <div className="totals" style={{ marginTop: 12 }}>
          <div><span>Данъчна основа</span><span>{money(netTotal, doc.currency)}</span></div>
          <div><span>ДДС</span><span>{money(vat, doc.currency)}</span></div>
          <div className="grand"><span>Общо</span><span>{money(total, doc.currency)}</span></div>
        </div>

        {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="primary" onClick={save} disabled={busy}>Запис на поправката</button>
          <button onClick={onCancel} disabled={busy}>Отказ</button>
        </div>
      </div>
    </div>
  );
}

function quantity2(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}
